import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Grid, CircularProgress } from "@mui/material";
import BreadCrumb from "../../../../components/navbar/Breadcrumb";
import { getProductoKae, updateProductoKae, getCategoriasKae } from "../../../../services";
import { withRouter } from "../../../../providers/withRouter";

const EditarProducto = () => {
  const { t } = useTranslation('common');
  const { id } = useParams();
  const navigate = useNavigate();
  const [producto, setProducto] = useState({});
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(true);

  const autoResize = (el) => {
    if (el) {
      el.style.height = "auto";
      el.style.height = el.scrollHeight + "px";
    }
  };

  const textareaRef = useCallback((node) => {
    if (node) autoResize(node);
  }, [loading]);

  useEffect(() => {
    Promise.all([fetchProducto(), fetchCategorias()]).finally(() => setLoading(false));
  }, []);

  const fetchProducto = async () => {
    try {
      const data = await getProductoKae(id);
      setProducto({
        nombre: data.nombre || "",
        sku: data.sku || "",
        descripcion: data.descripcion || "",
        categoria_id: data.categoria_id || "",
        precio_compra: data.precio_compra || "",
        precio_venta: data.precio_venta || "",
        // Sin esta linea el check de obligatoria salia vacio al abrir la señal
        obligatorio: !!data.obligatorio,
      });
    } catch (error) {
      console.log(error);
      toast.error(t('products.messages.loadError'));
    }
  };

  const fetchCategorias = async () => {
    try {
      const data = await getCategoriasKae();
      setCategorias(Array.isArray(data) ? data : []);
    } catch (error) {
      console.log(error);
    }
  };

  function handleSubmit(data) {
    const toastInfo = toast.info(t('products.messages.saving'));
    let errores = false;

    const requiredFields = [
      { key: 'nombre', label: t('products.columns.name') },
      { key: 'sku', label: t('products.columns.sku') },
      { key: 'descripcion', label: t('products.columns.description') },
      { key: 'categoria_id', label: t('products.columns.category') },
      { key: 'precio_compra', label: t('products.columns.purchasePrice') },
      { key: 'precio_venta', label: t('products.columns.salePrice') },
    ];

    requiredFields.forEach((field) => {
      if (!data[field.key] || (typeof data[field.key] === 'string' && !data[field.key].trim())) {
        toast.dismiss(toastInfo);
        toast.error(t('products.messages.fieldRequired', { field: field.label }));
        errores = true;
      }
    });

    if (!errores) {
      updateProductoKae(id, data)
        .then((res) => {
          if (res.status === 200) {
            toast.update(toastInfo, {
              render: t('products.messages.updated'),
              type: toast.TYPE.SUCCESS,
              autoClose: true,
            });
            navigate("/productos");
          } else {
            toast.update(toastInfo, {
              render: t('products.messages.updateError'),
              type: toast.TYPE.ERROR,
              autoClose: true,
            });
          }
        })
        .catch((err) => {
          console.log("ERROR", err);
          toast.error(t('products.messages.updateError'));
        });
    }
  }

  function handleChange(evt) {
    const value = evt.target.type === "checkbox" ? evt.target.checked : evt.target.value;
    setProducto({
      ...producto,
      [evt.target.name]: value,
    });
  }

  if (loading) {
    return <div style={{ textAlign: "center", padding: 40 }}><CircularProgress style={{ color: "#92004D" }} /></div>;
  }

  return (
    <>
      <BreadCrumb
        rutas={[
          { path: "/productos", label: t('products.title') },
          { path: `/productos/editar/${id}`, label: t('products.edit') },
        ]}
        style={{ margin: "0 10px" }}
      />
      <div className="pageContent">
        <div className="gridHead">
          {t('products.editProduct')}
        </div>
        <form className="nuevoAAFFForm">
          <Grid
            container
            spacing={5}
            style={{ marginTop: 20, marginBottom: 20, padding: "0 20px" }}
          >
            <Grid item xs={12} sm={6}>
              <input
                type="text"
                required
                placeholder={t('products.columns.name')}
                name="nombre"
                value={producto.nombre}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <input
                type="text"
                required
                placeholder={t('products.columns.sku')}
                name="sku"
                value={producto.sku}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6} sx={{ display: "flex", alignItems: "flex-end" }}>
              <select
                required
                name="categoria_id"
                value={producto.categoria_id}
                onChange={handleChange}
              >
                <option value="">{t('products.selectCategory')}</option>
                {categorias.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.nombre}
                  </option>
                ))}
              </select>
            </Grid>
            <Grid item xs={12} sm={3} sx={{ display: "flex", alignItems: "flex-end" }}>
              <input
                type="number"
                required
                placeholder={t('products.columns.purchasePrice')}
                name="precio_compra"
                value={producto.precio_compra}
                onChange={handleChange}
                step="0.01"
                min="0"
              />
            </Grid>
            <Grid item xs={12} sm={3} sx={{ display: "flex", alignItems: "flex-end" }}>
              <input
                type="number"
                required
                placeholder={t('products.columns.salePrice')}
                name="precio_venta"
                value={producto.precio_venta}
                onChange={handleChange}
                step="0.01"
                min="0"
              />
            </Grid>
            <Grid item xs={12}>
              <div style={{ position: "relative" }}>
                <textarea
                  ref={textareaRef}
                  required
                  placeholder={t('products.columns.description')}
                  name="descripcion"
                  value={producto.descripcion}
                  onChange={(e) => { handleChange(e); autoResize(e.target); }}
                  maxLength={255}
                  rows={1}
                  style={{ resize: "none", overflow: "hidden", minHeight: "40px" }}
                />
                <span style={{ position: "absolute", bottom: 8, right: 8, fontSize: "0.75rem", color: "#888" }}>
                  {(producto.descripcion || "").length}/255
                </span>
              </div>
            </Grid>
            <Grid item xs={12} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <input
                type="checkbox"
                id="obligatorio"
                name="obligatorio"
                checked={!!producto.obligatorio}
                onChange={handleChange}
                style={{ width: "auto", margin: 0 }}
              />
              <label htmlFor="obligatorio" style={{ margin: 0, cursor: "pointer" }}>
                {t('products.columns.mandatory')}
              </label>
            </Grid>
          </Grid>
          <Grid style={{ display: "flex", justifyContent: "end", padding: 20 }}>
            <input
              type="button"
              value={t('actions.save')}
              onClick={() => handleSubmit(producto)}
            />
          </Grid>
        </form>
      </div>
    </>
  );
};

export default withRouter(EditarProducto);
